/**
 * F8 · A shared list that is not a WhatsApp message.
 *
 * *"Groceries and errands, tablet and both phones, voice entry, live check-off.
 * Resist categories, budgets, history."*
 *
 * ## What is resisted, and why the resisting is the feature
 *
 * The spec spends three of its twenty words telling this file what not to do, and
 * every one of the three is the obvious next commit:
 *
 *  · **No categories.** Nobody files milk under Dairy on the way out of the door.
 *    A category is a decision demanded at the moment of typing, which is the moment
 *    the WhatsApp message wins, because a WhatsApp message asks for nothing.
 *  · **No budgets.** A running total turns a list into a negotiation. `05-features.md`
 *    rule 2: never make her the audience for a number she cannot act on, and a total
 *    that goes up as you add bread is exactly that.
 *  · **No history.** Checked items are deleted when the shop is done. Not archived,
 *    not "recently bought", not suggested next time. A list you have to scroll past
 *    last week's list to reach is a list you stop opening.
 *
 * So `ListItem` has four fields, and three of them are structural. There is nowhere
 * to put a category, a price or a completion date, which is deliberate: the shape of
 * the type is the argument, and it survives whoever edits this next.
 *
 * ## It is not shared yet, and it says so
 *
 * This is the honest half. Front Door has no server — `CLAUDE.md`: *"Deliberately
 * thin: no Firestore, no buckets. The client talks to fabric-api with the user's
 * token."* And `fabric-api` serves no list route; its whole route table was read
 * rather than guessed. The two ways to make this genuinely shared today are:
 *
 *  1. Add `GET/POST/PATCH /list` to `fabric-api`. That is a change in another repo.
 *  2. Write to Fabric's Firestore directly from this client. Rejected on the spec's
 *     own terms — `03-shell-and-layout.md`: *"Cross-portal links go through the
 *     other module's API with this module's service identity — never straight into
 *     its collections."*
 *
 * So the store is local to the device, and the panel says that in one sentence
 * rather than implying a sync that does not happen. That is not merely the least
 * bad option: `F25` requires the shopping list to work with no signal at all, so a
 * local store is the correct *primary* whatever happens next, and the missing piece
 * is a push and a pull on top of it. `SHARING` below names them.
 */
import type { Unwired } from "./sources";

/**
 * One line on the list. Four fields, and there is deliberately nowhere to add a
 * fifth without arguing with the header above.
 */
export type ListItem = {
  id: string;
  text: string;
  done: boolean;
  /** Added-at, for stable ordering only. Never rendered; it is not history. */
  at: number;
};

/** Bumped if the shape ever changes, so an old device does not read a new list wrongly. */
const KEY = "steyn.list.v1";

/** What "shared" is waiting on. */
export const SHARING: Unwired = {
  title: "On the phones",
  why: "This list is on this device only. Fabric serves no list route, and the Front Door has no server of its own.",
  next: "Add GET and POST /list to fabric-api. This file's read and write are the only things that change.",
};

class ShoppingList {
  items = $state<ListItem[]>([]);

  /**
   * False when the browser refuses storage — Safari in private mode throws on the
   * first write rather than the first read, so this is set from an actual probe.
   *
   * It matters because the failure is silent otherwise: items would appear, work
   * for the session, and vanish on reload with nobody told why.
   */
  persists = $state(true);

  constructor() {
    this.read();

    // The `storage` event fires in *other* tabs of the same origin, which is what
    // makes check-off live between the kitchen tablet's own tabs and a phone that
    // has the Front Door open twice. It is not sync between devices and is not
    // presented as such.
    if (typeof window !== "undefined") {
      addEventListener("storage", (e) => {
        if (e.key === KEY) this.read();
      });
    }
  }

  /** Unchecked first, in the order they were added. The order somebody shops in. */
  get open(): ListItem[] {
    return this.items.filter((i) => !i.done).sort((a, b) => a.at - b.at);
  }

  /**
   * Checked items stay visible until somebody clears them.
   *
   * Not history — the opposite. They are on screen so a mis-tap is visible and
   * reversible while you are still standing in the aisle, and they are gone the
   * moment `clearDone()` is pressed. Nothing is kept.
   */
  get done(): ListItem[] {
    return this.items.filter((i) => i.done).sort((a, b) => a.at - b.at);
  }

  /**
   * Add, unless it is already on the list.
   *
   * The duplicate check is case-insensitive and silent: two people adding "milk"
   * on the way home is the normal case, not an error worth a message. It returns
   * false so a caller can decide whether to flash the existing row instead.
   */
  add(text: string): boolean {
    const clean = text.trim().replace(/\s+/g, " ");
    if (!clean) return false;
    if (this.items.some((i) => !i.done && i.text.toLowerCase() === clean.toLowerCase())) return false;
    this.items = [...this.items, { id: crypto.randomUUID(), text: clean, done: false, at: Date.now() }];
    this.write();
    return true;
  }

  toggle(id: string): void {
    this.items = this.items.map((i) => (i.id === id ? { ...i, done: !i.done } : i));
    this.write();
  }

  remove(id: string): void {
    this.items = this.items.filter((i) => i.id !== id);
    this.write();
  }

  /** The end of a shop. Everything checked goes, and nothing is kept about it. */
  clearDone(): void {
    this.items = this.items.filter((i) => !i.done);
    this.write();
  }

  private read(): void {
    try {
      const raw = localStorage.getItem(KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      // Validated rather than trusted. This is the one input to this app that a
      // person can edit by hand, and a malformed row would otherwise render as
      // "undefined" on a kitchen wall.
      this.items = Array.isArray(parsed)
        ? parsed.filter(
            (i): i is ListItem =>
              !!i &&
              typeof (i as ListItem).id === "string" &&
              typeof (i as ListItem).text === "string" &&
              typeof (i as ListItem).done === "boolean" &&
              typeof (i as ListItem).at === "number",
          )
        : [];
    } catch {
      this.items = [];
      this.persists = false;
    }
  }

  private write(): void {
    try {
      localStorage.setItem(KEY, JSON.stringify(this.items));
      this.persists = true;
    } catch {
      // The list still works for this session; it just will not survive a reload.
      // Said out loud by the panel rather than swallowed.
      this.persists = false;
    }
  }
}

export const list = new ShoppingList();

// ── Voice entry ────────────────────────────────────────────────────────────────

/**
 * The Web Speech API, and nothing else.
 *
 * The brief was voice entry *if* it can be done with the browser API and no
 * external service, and it can: `SpeechRecognition` is built into the browser and
 * needs no key, no SDK and no account. Two honest caveats, both stated on screen
 * rather than only here:
 *
 *  · **It is not in every browser.** Chrome and Safari have it behind the `webkit`
 *    prefix; Firefox does not have it at all. The button is not rendered when it is
 *    absent, rather than rendered and broken.
 *  · **Chrome's implementation is not on-device.** It streams the audio to Google's
 *    speech service to transcribe it. That is the browser's behaviour, not an
 *    integration this repo added, but it is exactly the kind of thing a household
 *    should be told once — so the panel says the microphone goes through the
 *    browser's own service, and the button is never the only way to add an item.
 *
 * Typed locally because `lib.dom` does not ship a `SpeechRecognition` interface.
 * Minimal on purpose: only the four members actually used.
 */
type SpeechResultEvent = {
  results: ArrayLike<ArrayLike<{ transcript: string }>>;
};
type SpeechRecogniser = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  onresult: ((e: SpeechResultEvent) => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  onend: (() => void) | null;
};
type SpeechCtor = new () => SpeechRecogniser;

function recogniser(): SpeechCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { SpeechRecognition?: SpeechCtor; webkitSpeechRecognition?: SpeechCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Whether to render the button at all. Checked once, at module load. */
export const VOICE_AVAILABLE = recogniser() !== null;

class Voice {
  listening = $state(false);
  /** A plain sentence when something went wrong. Never a code. */
  note = $state<string | null>(null);

  private live: SpeechRecogniser | null = null;

  /**
   * Listen once, hand the words to `onText`, stop.
   *
   * Single-shot rather than continuous, deliberately. A microphone that stays open
   * on a kitchen wall is a microphone somebody unplugs, and `continuous` on a wall
   * tablet would mean the room's conversation being transcribed all evening. One
   * press, one item.
   */
  start(onText: (text: string) => void): void {
    const Ctor = recogniser();
    if (!Ctor || this.listening) return;

    this.note = null;
    const r = new Ctor();
    // en-ZA so "braai" and local place names stand a chance. Falls back to the
    // browser's own model when the locale is not supported; it does not fail.
    r.lang = "en-ZA";
    r.continuous = false;
    r.interimResults = false;

    r.onresult = (e) => {
      const said = e.results[0]?.[0]?.transcript ?? "";
      if (said.trim()) onText(said);
      else this.note = "Nothing was heard. Type it instead.";
    };
    r.onerror = (e) => {
      // Two of these are ordinary and must not read as faults: a person changing
      // their mind, and a browser that has not been given the microphone.
      this.note =
        e.error === "not-allowed"
          ? "The microphone is blocked for this site. Allow it in the browser, or type it instead."
          : e.error === "no-speech"
            ? "Nothing was heard. Type it instead."
            : "Voice did not work just now. Type it instead.";
    };
    r.onend = () => {
      this.listening = false;
      this.live = null;
    };

    try {
      r.start();
      this.live = r;
      this.listening = true;
    } catch {
      this.note = "Voice did not start. Type it instead.";
      this.listening = false;
    }
  }

  stop(): void {
    this.live?.stop();
    this.listening = false;
  }
}

export const voice = new Voice();
